/* eslint-disable @typescript-eslint/no-explicit-any */

import { homeSecurityPortalApi } from 'data/axios';
import {
  PRICE_TYPE,
  TAutoTopUpDataDetails,
  TAutoTopUpDataPayload,
  TCreateAutoTopUpDataPayload,
  TChangePaymentMethod,
  TChangePaymentMethodRes,
  TCheckoutSessionResponse,
  TCreateCheckoutSessionPayload,
  TCreateSubscriptionPayload,
  TCurrentDataPass,
  TDowngradePayload,
  TGetPoolDataRes,
  TPlanList,
  TProductType,
  TRetryPaymentRes,
  TSkipReason,
  TSubscriptionBuyType,
  TSubscriptionType,
  TTrialResponse,
  TUpgradPayload
} from 'typings';
import { CreatePurchaseId } from './paymentsApi';
import { ApiResponse } from './HttpRequest';
import { ATLAS_ENDPOINTS, PIGGYBANK_ENDPOINTS } from './endpoints';

export async function getSubscriptionDetails(): Promise<
  ApiResponse<TSubscriptionType[]>
> {
  const response = await homeSecurityPortalApi.get(
    ATLAS_ENDPOINTS.GET_SUBSCRIPTION
  );
  return response;
}

export async function getPurchasedInfoPolling(
  pollingAPI: string
): Promise<ApiResponse<any>> {
  const response = await homeSecurityPortalApi.get(pollingAPI);
  return response;
}

export async function DowngradeUserSubscription(
  subscriptionId: string,
  payload: TDowngradePayload
): Promise<ApiResponse<TSubscriptionBuyType>> {
  const response = await homeSecurityPortalApi.post(
    PIGGYBANK_ENDPOINTS.DOWNGRADE_SUBSCRIPTION(subscriptionId),
    payload
  );
  return response;
}

// CHECK : most probably we are not using this api
export async function BuyUserSubscription(
  payload: TUpgradPayload
): Promise<ApiResponse<TSubscriptionBuyType>> {
  const response = await homeSecurityPortalApi.post(
    PIGGYBANK_ENDPOINTS.BUY_SUBSCRIPTION,
    payload
  );
  return response;
}

export async function UpgradeUserSubscription(
  payload: TUpgradPayload
): Promise<ApiResponse<TSubscriptionBuyType>> {
  const response = await homeSecurityPortalApi.post(
    PIGGYBANK_ENDPOINTS.UPGRADE_SUBSCRIPTION(payload.purchase_id),
    payload
  );
  return response;
}

type TUpgradeUserSubscriptionV2 = {
  sku_id: string;
  payment_method_id?: string;
};

export async function UpgradeUserSubscriptionV2(
  subscriptionId: string,
  payload: TUpgradeUserSubscriptionV2
): Promise<ApiResponse<TSubscriptionBuyType>> {
  const response = await homeSecurityPortalApi.post(
    PIGGYBANK_ENDPOINTS.DOWNGRADE_SUBSCRIPTION_V2(subscriptionId),
    payload
  );
  return response;
}

// TODO: change PM in subscription api is needed for prime as well
export async function changePaymentMethodInSubscription(
  subscriptionId: string,
  payload: TChangePaymentMethod
): Promise<ApiResponse<TChangePaymentMethodRes>> {
  const response = await homeSecurityPortalApi.patch(
    PIGGYBANK_ENDPOINTS.CHANGE_PM_IN_SUBSCRIPTION(subscriptionId),
    payload
  );
  return response;
}

// Fetch one subscription by id for the /retryPayment banner (card details,
// amount, billing dates, status).
export async function getSubscriptionById(
  subscriptionId: string
): Promise<ApiResponse<TSubscriptionType>> {
  const response = await homeSecurityPortalApi.get(
    ATLAS_ENDPOINTS.SUBSCRIPTION_BY_ID(subscriptionId)
  );
  // atlas/subscriptions returns a list even with the `_id` filter, so unwrap
  // the single match here. Without this the banner reads fields off the array.
  const data = Array.isArray(response.data) ? response.data[0] : response.data;
  return { ...response, data };
}

// FCM-2514 retry CTA. Retries the failed charge against the given payment
// method (the card on file for a same-card retry, or a newly added card's
// payment method id). Resolves on 204; a 4xx/5xx rejects. The caller treats a
// resolved response as success and a rejected request as failure.
export async function retryPayment(
  subscriptionId: string,
  paymentMethodId: string
): Promise<ApiResponse<TRetryPaymentRes>> {
  const response = await homeSecurityPortalApi.post(
    PIGGYBANK_ENDPOINTS.RETRY_PAYMENT(subscriptionId),
    { payment_method_id: paymentMethodId }
  );
  return response;
}

// Create Subscription (Confluence "API implementations for Invoice and Payment
// method page"): POST piggybank/subscriptions with the purchase_id from Create
// Initial Purchase and the Stripe payment method to charge.
export async function createSubscription(
  payload: TCreateSubscriptionPayload
): Promise<ApiResponse<TSubscriptionBuyType>> {
  const response = await homeSecurityPortalApi.post(
    PIGGYBANK_ENDPOINTS.BUY_SUBSCRIPTION,
    payload
  );
  return response;
}

// Re-subscribe to the same plan — the Failed + Expired / RenewalFailed branch of
// the Confluence "Retry vs Re-subscribe logic" table. Two-step per spec: Create
// Initial Purchase from the failed subscription's plan, then Create Subscription
// against that new purchase with the chosen payment method. "Other data" (sku,
// space) is read straight off the purchase, since each subscription is a single
// purchase. `trial` is false and `price_type` is Recurring (it's a subscription).
export async function resubscribeSamePlan({
  subscription,
  country,
  paymentMethodId
}: {
  subscription: TSubscriptionType;
  country: string;
  paymentMethodId: string;
}): Promise<ApiResponse<TSubscriptionBuyType>> {
  const purchaseRes = await CreatePurchaseId({
    country,
    sku_id: subscription.sku_id,
    trial: false,
    space_id: subscription.space_id,
    price_type: PRICE_TYPE.RECURRING
  });
  const purchaseId = purchaseRes?.data?.purchase_id;
  if (!purchaseId) {
    throw new Error(
      'resubscribe: missing purchase_id from Create Initial Purchase'
    );
  }
  return createSubscription({
    purchase_id: purchaseId,
    stripe_payment_method: paymentMethodId
  });
}

// Poll the newly created purchase/subscription after a re-subscribe until it
// settles. Resolves true once it's Active, false on a Failed charge or once the
// attempts run out.
export function pollNewPurchaseActive(
  subscriptionId: string,
  attempts = 10,
  delayMs = 2000
): Promise<boolean> {
  return new Promise((resolve) => {
    let tries = 0;
    const tick = () => {
      tries += 1;
      getSubscriptionById(subscriptionId)
        .then(({ data }) => {
          if (data?.subscribe_status === 'Active') {
            resolve(true);
          } else if (data?.pay_status === 'Failed' || tries >= attempts) {
            resolve(false);
          } else {
            setTimeout(tick, delayMs);
          }
        })
        .catch(() => {
          if (tries >= attempts) resolve(false);
          else setTimeout(tick, delayMs);
        });
    };
    tick();
  });
}

export async function checkFreeTrialEligibility(
  productType: TProductType
): Promise<ApiResponse<TTrialResponse>> {
  const response = await homeSecurityPortalApi.get(
    PIGGYBANK_ENDPOINTS.CHECK_TRIAL(productType)
  );
  return response;
}

/** Whether the current space can start a data-subscription free trial. */
export async function checkDataFreeTrialEligibility(): Promise<
  ApiResponse<TTrialResponse>
> {
  const response = await homeSecurityPortalApi.get(
    PIGGYBANK_ENDPOINTS.CHECK_DATA_TRIAL
  );
  return response;
}

export const fetchCurrentPlanDetails = async ({
  skuId,
  country,
  price_type
}: {
  skuId: string;
  country: string;
  price_type: string;
}): Promise<ApiResponse<TPlanList>> => {
  const response = await homeSecurityPortalApi.get(
    PIGGYBANK_ENDPOINTS.GET_CURRENT_PLAN_SKU(skuId, country, price_type)
  );
  return response;
};

export async function createCheckoutSession(
  payload: TCreateCheckoutSessionPayload
): Promise<ApiResponse<TCheckoutSessionResponse>> {
  const response = await homeSecurityPortalApi.post(
    PIGGYBANK_ENDPOINTS.CHECKOUT_SESSION(payload?.purchase_id),
    payload
  );
  return response;
}

export async function cancelFreeTrialSubscription(subscriptionId: any) {
  const response = await homeSecurityPortalApi.delete(
    PIGGYBANK_ENDPOINTS.CANCEL_SUBSCRIPTION(subscriptionId)
  );
  return response;
}

export async function activateSubscription(subscriptionId: any) {
  const response = await homeSecurityPortalApi.patch(
    PIGGYBANK_ENDPOINTS.REACTIVATE_SUBSCRIPTION(subscriptionId)
  );
  return response;
}

// TODO : skip trial apis are also needed for prime

export async function FetchSkipFreeTrialList(
  productCategory: string,
  subscriptionType: string
) {
  try {
    const response = await homeSecurityPortalApi.get(
      PIGGYBANK_ENDPOINTS.SKIP_PLAN_REASON_LIST(
        productCategory,
        subscriptionType
      )
    );
    return response;
  } catch (error) {
    console.error('Error fetching skip free trial list:', error);
    throw error;
  }
}

export async function SkipFreeTrialAPI(payload: TSkipReason): Promise<any> {
  try {
    const response = await homeSecurityPortalApi.post(
      PIGGYBANK_ENDPOINTS.USER_SKIP_REASON,
      payload
    );
    return response.data;
  } catch (error) {
    console.error('Skip trial error:', error);
    throw error;
  }
}

export const fetchOneTimeManualTopupsPassDetails = async (
  spaceId: string,
  purhcaseType: string
): Promise<ApiResponse<TCurrentDataPass[]>> => {
  const response = await homeSecurityPortalApi.get(
    ATLAS_ENDPOINTS.GET_ONE_TIME_DATA_PASS_DETAILS(spaceId, purhcaseType)
  );
  return response;
};

export const fetchPoolData = async (
  spaceId: string
): Promise<ApiResponse<TGetPoolDataRes>> => {
  const response = await homeSecurityPortalApi.get(
    ATLAS_ENDPOINTS.GET_POOL_DATA(spaceId)
  );
  return response;
};

export const fetchDataAutoTopUp = async (
  spaceId: string
): Promise<ApiResponse<TAutoTopUpDataDetails>> => {
  const response = await homeSecurityPortalApi.get(
    ATLAS_ENDPOINTS.GET_AUTO_CHARGE_DETAILS(spaceId)
  );
  return response;
};

// Create returns 200 with an empty/null body (no payload).
export const createDataAutoTopUp = async (
  spaceId: string,
  payload: TCreateAutoTopUpDataPayload
): Promise<ApiResponse<null>> => {
  const response = await homeSecurityPortalApi.post(
    ATLAS_ENDPOINTS.CREATE_AUTO_CHARGE_DETAILS(spaceId),
    payload
  );
  return response;
};

export const updateDataAutoTopUp = async (
  spaceId: string,
  autoTopUpId: string,
  payload: TAutoTopUpDataPayload
): Promise<ApiResponse<TAutoTopUpDataDetails>> => {
  const response = await homeSecurityPortalApi.patch(
    ATLAS_ENDPOINTS.UPDATE_AUTO_CHARGE_DETAILS(spaceId, autoTopUpId),
    payload
  );
  return response;
};
